/**
 * DashboardParts — peças do Dashboard Sphere: MetricCard e BudgetPanel.
 *
 * MetricCard: `value === null` mostra "—" (ausente); 0 é mostrado como 0;
 * `loading` reserva o espaço com um skeleton estático (nunca número falso).
 * BudgetPanel: gasto × orçamento com `consumoDoOrcamento` (sem NaN/∞; a barra
 * enche até 100% e o texto mostra o efetivo, inclusive acima de 100%), série real
 * de 12 meses com o mês SELECIONADO pelo filtro de período (não fixo).
 */
import { useId } from 'react';
import { ChevronRight, Pencil } from 'lucide-react';
import {
  usd, consumoDoOrcamento, numeroOuNulo, rotuloDoMes,
} from '@/lib/format';

export function MetricCard({
  label, value, note, icon: Icon, tone = 'cyan', loading = false, trend, onClick, ariaLabel, mini, badge,
}) {
  const texto = value === null || value === undefined || value === '' ? '—' : String(value);
  const Tag = onClick ? 'button' : 'article';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      className="sp-metric"
      data-tone={tone}
      onClick={onClick}
      aria-label={onClick ? (ariaLabel || `${label}: ${texto}`) : undefined}
      aria-busy={loading || undefined}
    >
      <span className="sp-metric-icon" aria-hidden="true">{Icon && <Icon size={25} strokeWidth={1.75} />}</span>
      <div className="sp-metric-body">
        <div className="sp-metric-label">{label}{badge}</div>
        {loading ? (
          <span className="sp-metric-skeleton" aria-label={`${label}: carregando`} role="status" />
        ) : (
          <div className={`sp-metric-value${texto.length > 7 ? ' sp-metric-value--compact' : ''}`} data-testid={`metric-${label}`}>
            {texto}
          </div>
        )}
        {note && <div className="sp-metric-note" data-trend={trend}>{note}</div>}
      </div>
      {mini && <div className="sp-metric-mini" aria-hidden="true">{mini}</div>}
      {onClick && <ChevronRight className="sp-metric-chevron" size={18} strokeWidth={1.75} aria-hidden="true" />}
    </Tag>
  );
}

/** Mini linha (sparkline) a partir de valores REAIS; sem dados → nada. */
export function Sparkline({ valores }) {
  const v = (valores ?? []).map(numeroOuNulo).filter((x) => x != null);
  if (v.length < 2) return null;
  const max = Math.max(...v, 0);
  const min = Math.min(...v, 0);
  const span = max - min || 1;
  const pts = v.map((x, i) => `${(i / (v.length - 1)) * 60},${40 - ((x - min) / span) * 36}`).join(' ');
  return (
    <svg viewBox="0 0 60 44" preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
        style={{ filter: 'drop-shadow(0 0 4px currentColor)' }} />
    </svg>
  );
}

/**
 * @param {object} p
 * @param {number|null} p.gasto     custo do período (US$)
 * @param {number|null} p.limite    orçamento mensal (US$)
 * @param {Array<{mes:string, valor:number}>|null} p.serie  12 meses do ano do período
 * @param {string} p.periodo        mês selecionado YYYY-MM
 * @param {string} p.mesCorrente    mês corrente YYYY-MM (meses futuros ficam esmaecidos)
 * @param {(mes:string) => void} p.onPeriodo
 * @param {Array<string>} p.opcoesDePeriodo
 * @param {React.ReactNode} [p.editor]  formulário de edição (quando aberto)
 * @param {() => void} [p.onEditar]
 * @param {boolean} [p.loading] / p.serieIndisponivel
 */
export function BudgetPanel({
  gasto, limite, serie, periodo, mesCorrente, onPeriodo, opcoesDePeriodo = [], editor, onEditar,
  loading = false, serieIndisponivel = false,
}) {
  const uid = useId().replace(/:/g, '');
  const consumo = consumoDoOrcamento(gasto, limite);
  const valores = (serie ?? []).map((m) => numeroOuNulo(m.valor) ?? 0);
  const max = Math.max(1, ...valores);
  const pctTexto = consumo.ratio == null ? '—' : `${Math.round(consumo.ratio).toLocaleString('pt-BR')}%`;

  return (
    <section className="sp-panel sp-budget" aria-labelledby={`sp-budget-${uid}`}>
      <div className="sp-panel-header">
        <h2 id={`sp-budget-${uid}`}>Consumo de budget</h2>
        {onEditar && !editor && (
          <button type="button" className="sp-btn" onClick={onEditar}>
            <Pencil size={16} strokeWidth={1.75} aria-hidden="true" /> Editar orçamento
          </button>
        )}
      </div>

      <div className="sp-budget-total">
        <span>
          <strong data-testid="budget-gasto">{loading ? '—' : usd(gasto)}</strong> de <span data-testid="budget-limite">{usd(limite)}</span>
        </span>
        <span className="sp-percent" data-state={consumo.state} data-testid="budget-percentual">{pctTexto}</span>
      </div>
      <div
        className="sp-progress"
        role="progressbar"
        aria-label="Consumo do orçamento mensal"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={consumo.ratio == null ? undefined : Math.round(consumo.fill)}
        aria-valuetext={consumo.ratio == null
          ? 'Orçamento indisponível'
          : `${consumo.ratio.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% do orçamento${consumo.ratio > 100 ? ' — acima do limite' : ''}`}
      >
        <div className="sp-progress-fill" data-state={consumo.state} style={{ width: `${consumo.fill}%` }} />
      </div>

      {editor}

      <div className="sp-chart-head">
        <span>Gasto por mês</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>Período</span>
          <select className="sp-input" value={periodo} onChange={(e) => onPeriodo?.(e.target.value)} aria-label="Período">
            {opcoesDePeriodo.map((m) => <option key={m} value={m}>{rotuloDoMes(m, true)}</option>)}
          </select>
        </label>
      </div>

      {serieIndisponivel ? (
        <p className="sp-project-empty" role="status" style={{ padding: 16 }}>Série mensal indisponível no momento.</p>
      ) : (
        <ul className="sp-chart" aria-label="Gasto por mês no ano do período">
          {(serie ?? []).map((m) => {
            const v = numeroOuNulo(m.valor);
            const h = v == null ? 2 : Math.max(3, (v / max) * 92);
            const selecionado = m.mes === periodo;
            const futuro = mesCorrente && m.mes > mesCorrente;
            return (
              <li key={m.mes} className={`${selecionado ? 'is-selected' : ''}${futuro ? ' is-future' : ''}`}>
                <button
                  type="button"
                  onClick={() => !futuro && onPeriodo?.(m.mes)}
                  aria-pressed={selecionado}
                  disabled={futuro}
                  aria-label={`${rotuloDoMes(m.mes, true)}: ${futuro ? 'mês futuro' : usd(v)}`}
                  title={`${rotuloDoMes(m.mes, true)}: ${futuro ? 'mês futuro' : usd(v)}`}
                  style={{ '--bar-height': `${h}px` }}
                >
                  <span className="sp-bar" aria-hidden="true" />
                  <small aria-hidden="true">{rotuloDoMes(m.mes)}</small>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
